import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Manual theme preference (Light / Dark / System), persisted in localStorage,
// defaulting to LIGHT. Tailwind is in `class` mode, so we add/remove `dark` on
// <html>. A tiny inline script in index.html applies the same decision BEFORE
// first paint (no flash); this context initializes SYNCHRONOUSLY from the same
// storage key so React never disagrees with that initial class. 'system' follows
// the OS via matchMedia (only while selected). All storage access is defensive.
// ---------------------------------------------------------------------------

export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'akhara:theme';
const ORDER: ThemePref[] = ['light', 'dark', 'system'];

function readPref(): ThemePref {
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* storage blocked */
  }
  return 'light';
}
function systemDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}
function effectiveDark(pref: ThemePref): boolean {
  return pref === 'dark' || (pref === 'system' && systemDark());
}
function applyClass(pref: ThemePref): void {
  try {
    document.documentElement.classList.toggle('dark', effectiveDark(pref));
  } catch {
    /* no document (SSR) */
  }
}

interface ThemeValue {
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
  /** Cycle Light → Dark → System → Light. */
  cycle: () => void;
}

const ThemeContext = createContext<ThemeValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPref] = useState<ThemePref>(() => readPref()); // sync init — no flash

  // Apply the class + persist whenever the preference changes.
  useEffect(() => {
    applyClass(pref);
    try {
      window.localStorage.setItem(KEY, pref);
    } catch {
      /* persistence degrades silently */
    }
  }, [pref]);

  // Follow OS changes ONLY while 'system' is selected.
  useEffect(() => {
    if (pref !== 'system') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = () => applyClass('system');
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange); // Safari < 14
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [pref]);

  // Keep tabs in sync — another tab changed the preference.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setPref(readPref());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const cycle = useCallback(() => {
    setPref((cur) => ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]);
  }, []);

  const value = useMemo<ThemeValue>(() => ({ pref, setPref, cycle }), [pref, cycle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
